import { validateSipLogin } from './misc';

const SIP_SCHEME = 'sip:';
const TEL_SCHEME = 'tel:';
const TEL_BODY_REGEXP = /^\+?\d[\d().-]*$/;
const BRIDGE_TEL_BODY_REGEXP = /^\+7\d{10}$/;
const INVALID_CALL_TARGET_MESSAGE = 'Некорректный адрес вызова.';

/**
 * Нормализует `tel:`-адрес: убирает лишние пробелы и валидирует формат номера.
 *
 * @param rawTarget Сырой ввод пользователя.
 * @returns Нормализованный `tel:`-адрес или null, если формат некорректен.
 */
const normalizeTelTarget = (rawTarget: string): string | null => {
	const telBody = rawTarget.replace(/^tel:/i, '').trim().replaceAll(/\s+/g, '');
	if (!telBody || !TEL_BODY_REGEXP.test(telBody)) {
		return null;
	}

	if (telBody.includes('+') && !telBody.startsWith('+')) {
		return null;
	}

	return `${TEL_SCHEME}${telBody}`;
};

/**
 * Нормализует bridge-телефон до строгого `tel:+7XXXXXXXXXX`.
 *
 * Принимает:
 * - `tel:+7...`
 * - `+7...` (в т.ч. с пробелами/скобками/дефисами)
 *
 * @param rawTarget Сырой телефонный адрес.
 * @returns Нормализованный `tel:+7...` или null.
 */
const normalizeBridgeTelTarget = (rawTarget: string): string | null => {
	const telBody = rawTarget.replace(/^tel:/i, '').trim();
	if (!telBody || /[^\d\s()+.-]/.test(telBody)) {
		return null;
	}

	const compactBody = telBody.replaceAll(/[\s().-]/g, '');
	if (!BRIDGE_TEL_BODY_REGEXP.test(compactBody)) {
		return null;
	}

	return `${TEL_SCHEME}${compactBody}`;
};

/**
 * Разбирает bridge-цель вызова.
 *
 * Поддерживаются:
 * - `sip:user@domain` (с валидацией SIP URI);
 * - `tel:+7XXXXXXXXXX`;
 * - `+7XXXXXXXXXX` (без схемы, будет нормализован в `tel:+7...`).
 *
 * @param rawTarget Сырой адрес для bridge-вызова.
 * @returns Нормализованный `sip:*` или `tel:+7XXXXXXXXXX`.
 * @throws Error Если адрес не соответствует ожидаемому формату.
 */
export const parseBridgeCallTarget = (rawTarget: string): string => {
	const normalizedInput = rawTarget.trim();
	if (!normalizedInput) {
		throw new Error(INVALID_CALL_TARGET_MESSAGE);
	}

	const lowerCasedInput = normalizedInput.toLowerCase();
	if (lowerCasedInput.startsWith(SIP_SCHEME)) {
		if (!validateSipLogin(normalizedInput, [])) {
			throw new Error(`Некорректный SIP-адрес: ${normalizedInput}`);
		}

		return normalizedInput;
	}

	const maybeTelInput = lowerCasedInput.startsWith(TEL_SCHEME)
		? normalizedInput
		: normalizedInput.startsWith('+')
			? `${TEL_SCHEME}${normalizedInput}`
			: null;

	if (!maybeTelInput) {
		throw new Error(`Некорректный телефонный адрес: ${normalizedInput}`);
	}

	const normalizedBridgeTarget = normalizeBridgeTelTarget(maybeTelInput);
	if (!normalizedBridgeTarget) {
		throw new Error(`Некорректный телефонный адрес: ${normalizedInput}`);
	}

	return normalizedBridgeTarget;
};

/**
 * Разбирает и валидирует цель вызова в форматах `sip:` и `tel:`.
 *
 * @param rawTarget Сырой адрес для звонка.
 * @returns Нормализованный SIP/TEL-адрес.
 * @throws Error Если адрес не соответствует ожидаемому формату.
 */
export const parseCallTarget = (rawTarget: string): string => {
	const normalizedInput = rawTarget.trim();
	const lowerCasedInput = normalizedInput.toLowerCase();

	if (lowerCasedInput.startsWith(SIP_SCHEME)) {
		if (!validateSipLogin(normalizedInput, [])) {
			throw new Error(`Некорректный SIP-адрес: ${normalizedInput}`);
		}

		return normalizedInput;
	}

	if (lowerCasedInput.startsWith(TEL_SCHEME)) {
		const normalizedTelTarget = normalizeTelTarget(normalizedInput);
		if (!normalizedTelTarget) {
			throw new Error(`Некорректный телефонный адрес: ${normalizedInput}`);
		}

		return normalizedTelTarget;
	}

	throw new Error(INVALID_CALL_TARGET_MESSAGE);
};
